import type { ConnectionRouteInfo } from '../types/nodeConfig';
import type { ProjectNode, ProjectRoute } from '../types/project';
import { formatRouteLabel } from './routeLabels';

// ------------------------------------------------------------
// Gate open/closed route resolution (matches editor + simulation)
// ------------------------------------------------------------

export function outgoingRoutesForGate(
  gateNodeId: string,
  routes: ProjectRoute[],
  nodes: ProjectNode[],
): ConnectionRouteInfo[] {
  return routes
    .filter((route) => route.from_node_id === gateNodeId)
    .map((route) => {
      const peer = nodes.find((node) => node.id === route.to_node_id);
      return {
        route_id: route.id,
        route_name: route.name,
        peer_node_id: route.to_node_id,
        peer_node_name: peer?.name ?? route.to_node_id,
      };
    });
}

export function resolveGateRouteIds(
  node: ProjectNode,
  outgoing: ConnectionRouteInfo[],
): { openRouteId?: string; closedRouteId?: string } {
  const openIndex = Number(node.open_route_index ?? 0);
  const closedIndex = Number(node.closed_route_index ?? 1);
  const openFromIndex = outgoing[openIndex]?.route_id;
  const closedFromIndex =
    outgoing[closedIndex]?.route_id ??
    outgoing[1]?.route_id ??
    outgoing[0]?.route_id;

  let openRouteId =
    (node.open_route_id as string | undefined) ?? openFromIndex;
  let closedRouteId =
    (node.closed_route_id as string | undefined) ?? closedFromIndex;

  // Legacy projects may store the same route id for both states while indices differ.
  if (
    openRouteId &&
    closedRouteId &&
    openRouteId === closedRouteId &&
    outgoing.length > 1 &&
    openFromIndex &&
    closedFromIndex &&
    openFromIndex !== closedFromIndex
  ) {
    openRouteId = openFromIndex;
    closedRouteId = closedFromIndex;
  }

  return { openRouteId, closedRouteId };
}

export function gateRouteLabel(
  routeId: string | undefined,
  outgoing: ConnectionRouteInfo[],
): string {
  if (!routeId) return '—';
  const route = outgoing.find((item) => item.route_id === routeId);
  if (!route) return routeId;
  return formatRouteLabel(route, 'outgoing');
}

export function gateRoutesConfiguredSame(
  openRouteId?: string,
  closedRouteId?: string,
): boolean {
  return Boolean(openRouteId && closedRouteId && openRouteId === closedRouteId);
}
